import { ApiService } from '../ApiService';
import type { Declaration, DeclarationAttachment } from '../../../types/declarations';
import type { PaginatedResponse } from '../types';

interface DeclarationFilters {
  type?: string;
  category?: string;
  status?: string;
  period?: string;
  fiscalYearId?: string;
  page?: number;
  pageSize?: number;
}

export const declarationsApi = {
  // CRUD Operations
  getAll: (filters?: DeclarationFilters) => 
    ApiService.get<PaginatedResponse<Declaration>>('/declarations', filters),

  getById: (id: string) => 
    ApiService.get<Declaration>(`/declarations/${id}`),

  create: (declaration: Partial<Declaration>) => 
    ApiService.post<Declaration>('/declarations', declaration),

  update: (id: string, declaration: Partial<Declaration>) => 
    ApiService.put<Declaration>(`/declarations/${id}`, declaration),

  delete: (id: string) => 
    ApiService.delete(`/declarations/${id}`),

  // Status Management
  submit: (id: string) => 
    ApiService.post<Declaration>(`/declarations/${id}/submit`, {}),

  validate: (id: string, validatorId: string) => 
    ApiService.post<Declaration>(`/declarations/${id}/validate`, { validatorId }),

  reject: (id: string, reason: string) => 
    ApiService.post<Declaration>(`/declarations/${id}/reject`, { reason }),

  // Document Management
  uploadJustification: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('justification', file);
    return ApiService.post<{ url: string }>(`/declarations/${id}/justification`, formData);
  },

  uploadDeclarationForm: (id: string, file: File) => {
    const formData = new FormData();
    formData.append('declaration_form', file);
    return ApiService.post<{ url: string }>(`/declarations/${id}/declaration-form`, formData);
  },

  uploadAttachment: (id: string, file: File, type: DeclarationAttachment['type']) => {
    const formData = new FormData();
    formData.append('attachment', file);
    formData.append('type', type);
    return ApiService.post<DeclarationAttachment>(`/declarations/${id}/attachments`, formData);
  },

  getAttachments: (id: string) => 
    ApiService.get<DeclarationAttachment[]>(`/declarations/${id}/attachments`),

  deleteAttachment: (declarationId: string, attachmentId: string) => 
    ApiService.delete(`/declarations/${declarationId}/attachments/${attachmentId}`),

  // Export & Download
  download: (id: string, format: 'pdf' | 'excel') => 
    ApiService.get<Blob>(`/declarations/${id}/download?format=${format}`),

  downloadJustification: (id: string) => 
    ApiService.get<Blob>(`/declarations/${id}/justification/download`),

  downloadDeclarationForm: (id: string) => 
    ApiService.get<Blob>(`/declarations/${id}/declaration-form/download`),

  downloadAttachment: (declarationId: string, attachmentId: string) => 
    ApiService.get<Blob>(`/declarations/${declarationId}/attachments/${attachmentId}/download`),

  // Reporting
  getStatistics: (period?: string) => 
    ApiService.get<{
      totalDeclarations: number;
      totalAmount: number;
      byStatus: Record<string, number>;
      byType: Record<string, number>;
      overdueCount: number;
    }>('/declarations/statistics', { period }),

  // Templates & Forms
  getDeclarationTemplate: (type: string) => 
    ApiService.get<Blob>(`/declarations/templates/${type}`),

  // Validation
  validateDeclarationData: (data: Partial<Declaration>) => 
    ApiService.post<{ valid: boolean; errors: string[] }>('/declarations/validate', data)
};